/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { FC, useContext, useEffect, useMemo, useState } from 'react';
import { PointElement } from 'chart.js';
import clsx from 'clsx';
import { add, differenceInDays, endOfWeek, isBefore, startOfDay, startOfWeek } from 'date-fns';
import { capitalize } from 'lodash';

import { formatCurrency, useIntl } from '@futrli/localisation';
import { PALETTE_COLOURS } from '@futrli/ui-components';

import { formatlocalDate } from '../../../../../../core/formatters';
import { MAX_DIGITS_IN_CELL, SIZES } from '../../../../../../core/constants';
import { PredictionsAccountCategories } from '../../../../../../core/types';
import { usePredictionsInfoContext } from '../../../../../../forecast/detail/context/predictions-info-context';
import { PredictManagementContext } from '../../../../../../predict-management/context/predict-management-context';
import { UILineChart } from '../../../../../../ui/charts/line-chart/line-chart';
import { Cell } from '../../../../../../ui/data-grid/components/cell';
import { isCurrentPeriod } from '../../../../../../ui/data-grid/components/current-period/current-period';
import { useDataGridLayout } from '../../../../../../ui/data-grid/components/data-grid-layout-context';
import { NOT_ALLOWED_NUMBER_EXPLAINED } from '../../../../../../ui/data-grid/components/number-explained/number-explained-messages';
import { forceLocalDate } from '../../../../../../utils/dates';
import { UserGridSettingsContext } from '../../../user-grid-settings/user-grid-settings-context';
import { ROW_KIND } from '../../constants';
import { getFYIndexes } from '../../format-rows';
import { nestedAccountHeightOverride } from '../../style-rows';
import { JoinedCellProps, ParentRowType } from '../../types';

import grid from '../../../../../../ui/data-grid/components/grid.scss';

interface Props {
  chartCustomSettings?: { colors?: string[]; thinLines?: boolean; yesterday?: boolean };
  row: ParentRowType;
  rowHeight: SIZES;
  showDecimals: boolean;
  rowHeightOverride?: SIZES;
  level: number;
  showZeroAndHighlightForChart?: boolean;
  columns: string[];
  category?: PredictionsAccountCategories;
  hideBudgetData?: boolean;
  autoScrollMarker?: boolean;
}
export const CHART_HEIGHT = 50;
export const CHART_HEIGHT_XLARGE = 100;

export const RowAccount: FC<Props> = ({
  category,
  columns,
  rowHeight,
  rowHeightOverride,
  row,
  level,
  showDecimals,
  chartCustomSettings,
  hideBudgetData = false,
  autoScrollMarker = false
}: Props) => {
  const { periodType, forecast, forecastGraphDate } = usePredictionsInfoContext();
  const dataGridLayout = useDataGridLayout();
  const { expandedRows } = useContext(UserGridSettingsContext);
  const { currency } = useContext(PredictManagementContext);
  const intl = useIntl();
  const [chartData, setChartData] = useState<{ data: (number | null)[] }[]>([]);
  const [chartLabels, setChartLabels] = useState<string[]>([]);
  const [markerIndex, setMarkerIndex] = useState<number | undefined>();

  useEffect(() => {
    if (row.kind == ROW_KIND.total && row.dailyData && row.dailyColumns) {
      const data: (number | null)[] = [];
      const labels: string[] = [];
      const dateToCheck = startOfDay(forceLocalDate(forecastGraphDate || ''));
      const firstAvailableDayBits = row.dailyColumns[0].split('-');
      if (firstAvailableDayBits.length === 3) {
        let offsetDays = +firstAvailableDayBits[2];
        if (periodType === 'WEEKLY_MONDAY') {
          const startOfTheWeek = add(startOfWeek(new Date(row.dailyColumns[0]), { weekStartsOn: 0 }), { days: 1 });
          offsetDays = differenceInDays(new Date(row.dailyColumns[0]), startOfTheWeek);
        }
        if (periodType === 'WEEKLY_SUNDAY') {
          const startOfTheWeek = add(startOfWeek(new Date(row.dailyColumns[0]), { weekStartsOn: 6 }), { days: 1 });
          offsetDays = differenceInDays(new Date(row.dailyColumns[0]), startOfTheWeek);
        }
        if (!isNaN(offsetDays) && offsetDays > 1) {
          Array.from(Array(offsetDays - 1).keys()).forEach(n => {
            data.push(null);
            labels.push(`${firstAvailableDayBits[0]}-${firstAvailableDayBits[1]}-${n < 9 ? '0' : ''}${n + 1}`);
          });
        }
      }

      let indexMarker = -1;
      const offset = labels.length;
      row.dailyData.forEach((value: string, index: number) => {
        data.push(value !== null ? Math.round(+value / 100) : value);
        labels.push(row.dailyColumns?.[index] || '');
        if (
          indexMarker === -1 &&
          row.dailyColumns &&
          !isBefore(startOfDay(forceLocalDate(row.dailyColumns[index])), dateToCheck)
        ) {
          indexMarker = index;
        }
      });
      const lastAvailableDayBits = row.dailyColumns[row.dailyColumns.length - 1].split('-');
      if (lastAvailableDayBits.length === 3) {
        let rightOffsetDays = 30 - +lastAvailableDayBits[2];
        if (periodType === 'WEEKLY_MONDAY') {
          const endOfTheWeek = add(
            endOfWeek(new Date(row.dailyColumns[row.dailyColumns.length - 1]), { weekStartsOn: 0 }),
            { days: 1 }
          );
          rightOffsetDays = differenceInDays(endOfTheWeek, new Date(row.dailyColumns[row.dailyColumns.length - 1]));
        }
        if (periodType === 'WEEKLY_SUNDAY') {
          const endOfTheWeek = add(
            endOfWeek(new Date(row.dailyColumns[row.dailyColumns.length - 1]), { weekStartsOn: 6 }),
            { days: 1 }
          );
          rightOffsetDays = differenceInDays(endOfTheWeek, new Date(row.dailyColumns[row.dailyColumns.length - 1]));
        }
        if (!isNaN(rightOffsetDays) && rightOffsetDays > 0) {
          Array.from(Array(rightOffsetDays).keys()).forEach(i => {
            const n = rightOffsetDays + i;
            data.push(null);
            labels.push(`${lastAvailableDayBits[0]}-${lastAvailableDayBits[1]}-${n < 9 ? '0' : ''}${n + 1}`);
          });
        }
      }

      setChartData([{ data }]);
      setChartLabels(labels);
      setMarkerIndex(indexMarker + offset);
    } else if (row.kind == ROW_KIND.total && row?.account?.period_data) {
      const consolidatedData: (number | null)[] = [];
      const consolidatedLabels: string[] = [];
      row.account.period_data.forEach((value: string, index: number) => {
        consolidatedData.push(Math.round(+value / 100));
        consolidatedLabels.push(columns[index]);
      });

      setChartData([{ data: consolidatedData }]);
      setChartLabels(consolidatedLabels);
    }
  }, [row, forecastGraphDate, periodType, columns]);

  const fyIndexes = useMemo(() => getFYIndexes(columns), [columns]);

  if (!periodType) {
    return null;
  }

  const { isBold, childCount, isFirst, isLast, forceRowSize } = row;
  const usedRowHeight = rowHeightOverride || rowHeight;
  const calcRowHeightOverride = nestedAccountHeightOverride(row, usedRowHeight, level);
  const isDisabled = row.isDisabled;
  const isChild = row.level > 0;
  const hasVisibleChildren = expandedRows.includes(row.account?.index) && childCount >= 1;
  const chartHeight = rowHeightOverride === SIZES.X_LARGE ? CHART_HEIGHT_XLARGE : CHART_HEIGHT;

  const rowClasses = clsx({
    [grid.row]: true,
    [grid[`rowLevel${level}`]]: true,
    [grid.isChild]: isChild,
    [grid.isLast]: row.isLast,
    [grid[`is${capitalize(row.kind)}`]]: true,
    [grid.hasForceRowSize]: Boolean(forceRowSize),
    [grid.isMainHeadline]: Boolean(row.isMainHeadline),
    [grid.isBold]: isBold
  });

  const isNumberExplainedAllowed = (cellDateIndex: string) => {
    return (
      [ROW_KIND.total, ROW_KIND.account].includes(row.kind) &&
      isCurrentPeriod(cellDateIndex, periodType, forecastGraphDate)
    );
  };

  const colorFn = (pastColor: string, positiveColor: string, negativeColor: string) => (ctx: {
    p0: PointElement;
    p0DataIndex: number;
  }) => {
    const dateToCheck = startOfDay(forceLocalDate(forecastGraphDate || ''));

    const p = (ctx.p0 as unknown) as { raw: number };
    let isPast = false;
    if (row.kind == ROW_KIND.total && chartLabels.length) {
      isPast = isBefore(startOfDay(forceLocalDate(chartLabels[ctx.p0DataIndex])), startOfDay(dateToCheck));
    }
    return isPast ? pastColor : p.raw >= 0 ? positiveColor : negativeColor;
  };

  const hasDailyData =
    (row.kind == ROW_KIND.total && row.dailyData && row.dailyColumns) ||
    (row.kind === ROW_KIND.total && row.account.period_data);

  return (
    <div className={rowClasses} style={dataGridLayout ? { width: '100%' } : undefined} role="cell">
      <div
        className={grid.rowInner}
        style={{
          backgroundColor: hideBudgetData ? '#f6f7fa' : 'white',
          ...(dataGridLayout ? { width: '100%' } : {})
        }}
      >
        {row.account?.period_data?.map((cellValue, index) => {
          if (hideBudgetData) {
            return <div key={`${cellValue}-${index}`} style={{ width: 'var(--data-grid-column-width, 96px)' }} />;
          }

          const cellDateIndex = columns[index];

          // if no matching column we shouldn't render a cell as it will be outside the window
          // this should only happen when rendering P+L totals on cash impact
          if (cellDateIndex == undefined) {
            return null;
          }

          const isCurrentPeriodCell =
            forecast?.kind !== 'budget' && isCurrentPeriod(cellDateIndex, periodType, forecastGraphDate);

          const cellProps = {
            category,
            periodType,
            index: cellDateIndex,
            value: cellValue,
            periodToDateValue: row.account.period_to_date,
            isPercentage: row.kind == ROW_KIND.calculation && row.percentage,
            isColored: row.kind === ROW_KIND.total && row.account?.static_id === 'net_cash_flow',
            isDisabled,
            isChild,
            isCurrentPeriod: isCurrentPeriodCell,
            isBold,
            isLevel1: level === 1,
            isFirst,
            isLast: isLast && !hasVisibleChildren,
            isExpanded: hasVisibleChildren,
            maxDigits: MAX_DIGITS_IN_CELL,
            rowHeight,
            showDecimals,
            rowHeightOverride: calcRowHeightOverride,
            forceRowSize,
            numberExplained: !isNumberExplainedAllowed(cellDateIndex)
              ? NOT_ALLOWED_NUMBER_EXPLAINED
              : {
                  allowed: true,
                  data: {
                    reportSection: row.section,
                    accountId: row.kind === ROW_KIND.total ? row.account?.index : row.account.canonical_category,
                    type: row.kind,
                    accountName: row.account.name
                  }
                },
            calculatedDateToCheck: forecastGraphDate,
            isFY: fyIndexes.indexOf(index) !== -1
          };

          let joinedCellProps: JoinedCellProps | undefined;
          if (row.kind == 'calculation' && row.joinedRow) {
            const joinedCellValue = row.joinedRow.account.period_data?.[index];

            joinedCellProps = {
              isPercentage: row.joinedRow.percentage,
              index: cellDateIndex,
              maxDigits: MAX_DIGITS_IN_CELL,
              value: joinedCellValue
            };
          }
          return <Cell key={cellDateIndex} joinedCell={joinedCellProps} {...cellProps} />;
        })}
        {!hideBudgetData &&
          !isDisabled &&
          [SIZES.LARGE, SIZES.X_LARGE].includes(calcRowHeightOverride) &&
          hasDailyData && (
            <UILineChart
              ariaLabel="Cash position line chart"
              height={chartHeight}
              labels={chartLabels}
              showYesterdayMarker={chartCustomSettings ? chartCustomSettings.yesterday : (markerIndex || 0) > 0}
              showMarker={chartCustomSettings ? !chartCustomSettings.yesterday && (markerIndex || 0) > 0 : false}
              datasets={chartData}
              lineColorFn={colorFn(
                PALETTE_COLOURS.$chartGrey,
                chartCustomSettings?.colors?.[0] || PALETTE_COLOURS.$chartLimeGreen,
                chartCustomSettings?.colors?.[0] || PALETTE_COLOURS.$chartSoftRed
              )}
              areaColorFn={colorFn(
                PALETTE_COLOURS.$chartLightGrey,
                chartCustomSettings?.colors?.[1] || PALETTE_COLOURS.$chartLightCyan,
                chartCustomSettings?.colors?.[1] || PALETTE_COLOURS.$chartLightRed
              )}
              markerIndex={markerIndex}
              dateFormatter={(date: string) => formatlocalDate(date, intl)}
              currencyFormatter={(value: number) => formatCurrency(value, intl, currency || '', 0, 0)}
              absolute
              thinLines={chartCustomSettings?.thinLines}
              autoScrollToMarker={autoScrollMarker}
            />
          )}
      </div>
    </div>
  );
};
